import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import { useAppContext } from "@/context/AppContext";
import { useNavigation } from "@/context/NavigationContext";
import AircraftPartCard from "@/components/AircraftPartCard";
import InstrumentLoader from "@/components/InstrumentLoader";
import { PART_CATEGORIES } from "../enums";
import {
  Search,
  Filter,
  Package,
  RotateCcw,
  ChevronDown,
  X,
  Check,
  Activity
} from "lucide-react";

type SortOption = "price-asc" | "price-desc" | "title-asc" | "qty-desc";

interface StateOption {
  code: string;
  label: string;
}

const AircraftPartCategoryPage = () => {
  const { navigateTo } = useNavigation();
  const { getDictValue, getDictJson } = useAuth();

  // 🟢 Extract parts state, fetch function, and loader from AppContext
  const {
    aircraftParts,
    fetchAircraftParts,
    isAircraftPartsLoading
  } = useAppContext();

  const [search, setSearch] = useState("");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [sort, setSort] = useState<SortOption>("price-asc");
  const [selectedStates, setSelectedStates] = useState<string[]>([]);
  const [isCatOpen, setIsCatOpen] = useState(false);

  // 🟢 Preluare dinamică a opțiunilor de stare din dicționar
  const partStatesList = useMemo(() => {
    return getDictJson<StateOption[]>("listing.part.filter.states", []);
  }, [getDictJson]);

  // 🟢 Fetch parts on component mount when the page opens
  useEffect(() => {
    fetchAircraftParts().catch((error) => {
      console.error("Failed to fetch aircraft parts on page load:", error);
    });
  }, [fetchAircraftParts]);

  const filteredParts = useMemo(() => {
    if (isAircraftPartsLoading) return [];
    let result = [...aircraftParts];

    // 1. Căutare text (Titlu, Producător, S/N)
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(p =>
        p.title?.toLowerCase().includes(q) ||
        p.manufacturer?.toLowerCase().includes(q) ||
        p.serialNumber?.toLowerCase().includes(q) ||
        p.description?.toLowerCase().includes(q)
      );
    }

    // 2. Filtru Categorie
    if (selectedCategories.length > 0) {
      result = result.filter(p => p.category && selectedCategories.includes(p.category));
    }

    // 3. Filtru Stare (NEW, OH, etc)
    if (selectedStates.length > 0) {
      result = result.filter(p => p.state && selectedStates.includes(p.state));
    }

    // 4. Filtru Preț
    if (priceMin) result = result.filter(p => Number(p.grossValue || (p as any).price || 0) >= Number(priceMin));
    if (priceMax) result = result.filter(p => Number(p.grossValue || (p as any).price || 0) <= Number(priceMax));

    // 5. Sortare
    switch (sort) {
      case "price-asc":
        result.sort((a, b) => {
          const valA = Number(a.grossValue || (a as any).price);
          const valB = Number(b.grossValue || (b as any).price);
          const pA = (!isNaN(valA) && valA > 0) ? valA : Number.MAX_VALUE;
          const pB = (!isNaN(valB) && valB > 0) ? valB : Number.MAX_VALUE;
          return pA - pB;
        });
        break;
      case "price-desc":
        result.sort((a, b) => {
          const valA = Number(a.grossValue || (a as any).price);
          const valB = Number(b.grossValue || (b as any).price);
          const pA = (!isNaN(valA) && valA > 0) ? valA : -1;
          const pB = (!isNaN(valB) && valB > 0) ? valB : -1;
          return pB - pA;
        });
        break;
      case "qty-desc":
        result.sort((a, b) => (b.quantity || 0) - (a.quantity || 0));
        break;
      case "title-asc":
        result.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
        break;
    }

    return result;
  }, [aircraftParts, search, selectedCategories, selectedStates, priceMin, priceMax, sort, isAircraftPartsLoading]);

  return (
    <div className="min-h-screen bg-[#020617] text-slate-300 pb-20 font-sans">
      <InstrumentLoader visible={isAircraftPartsLoading} />

      <div className="container max-w-7xl py-12 px-6">
        {/* HEADER - Green Theme */}
        <header className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 border-l-4 border-emerald-500 pl-6">
          <div>
            <p className="text-emerald-500 font-mono text-sm tracking-[0.2em] mb-1 uppercase italic">
              {getDictValue("listing.part.header") || "Marketplace // Piese de vânzare"}
            </p>
            <h1 className="text-5xl font-black text-white tracking-tight italic uppercase">
              {getDictValue("listing.part.title") || "PIESE & COMPONENTE"}
            </h1>
          </div>
          <div className="flex items-center gap-4 bg-slate-900/50 p-2 rounded-2xl border border-slate-800">
            <div className="text-right px-4 border-r border-slate-800">
              <p className="text-[10px] text-slate-500 uppercase font-bold tracking-widest">
                {getDictValue("listing.part.filter.match.txb") || "Unități găsite"}
              </p>
              <p className="text-emerald-500 font-mono text-xl font-black">{filteredParts.length}</p>
            </div>
            <Package className="text-slate-700 mr-2" size={20} />
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* ASIDE FILTERS */}
          <aside className="lg:col-span-3 space-y-6 sticky top-24 h-[calc(100vh-120px)] overflow-y-auto no-scrollbar pb-10">
            <div className="bg-[#0F172A]/80 backdrop-blur-xl border border-slate-800 rounded-3xl p-6 shadow-2xl">

              <div className="flex items-center justify-between mb-6 border-b border-slate-800/50 pb-4">
                <div className="flex items-center gap-2">
                  <Filter size={16} className="text-emerald-500" />
                  <h3 className="text-xs font-black uppercase tracking-widest text-white">
                    {getDictValue("listing.part.filter.title") || "Filtre"}
                  </h3>
                </div>
                {(search || selectedCategories.length > 0 || selectedStates.length > 0 || priceMin || priceMax) && (
                  <button
                    onClick={() => {
                      setSearch(""); setSelectedCategories([]); setSelectedStates([]); setPriceMin(""); setPriceMax("");
                    }}
                    className="text-[10px] text-red-500 font-bold uppercase flex items-center gap-1 active:scale-95 transition-all"
                  >
                    <RotateCcw size={12} /> Reset
                  </button>
                )}
              </div>

              <div className="space-y-8">
                {/* Search */}
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2 block ml-1">
                    {getDictValue("listing.part.filter.id.txb") || "Tip / S/N"}
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={14} />
                    <input
                      className="w-full h-11 pl-10 pr-4 rounded-xl bg-[#1E293B] border border-slate-700 text-sm text-white focus:border-emerald-500 outline-none transition-all placeholder:text-slate-600"
                      placeholder={getDictValue("listing.part.filter.id.placeholder") || "Ex: Motor, Garmin..."}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>
                </div>

                {/* Categories - Custom Multi-Select */}
                <div className="relative">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2 block ml-1">
                    {getDictValue("listing.part.filter.category.txb") || "Categorie"}
                  </label>

                  {/* Trigger Button */}
                  <div
                    onClick={() => setIsCatOpen(!isCatOpen)}
                    className="min-h-[44px] w-full p-2 rounded-xl bg-[#1E293B] border border-slate-700 flex flex-wrap gap-1 items-center cursor-pointer hover:border-slate-600 transition-all relative pr-10"
                  >
                    {selectedCategories.length === 0 ? (
                      <span className="text-sm text-slate-500 ml-2 italic">Toate</span>
                    ) : (
                      selectedCategories.map(catCode => {
                        const catLabel = PART_CATEGORIES.find(c => c.code === catCode)?.label;
                        return (
                          <span
                            key={catCode}
                            className="flex items-center gap-1 bg-emerald-600/20 border border-emerald-500/30 text-emerald-400 text-[10px] font-bold px-2 py-1 rounded-md animate-in zoom-in-95 duration-150"
                          >
                            {catLabel}
                            <X
                              size={12}
                              className="hover:text-white transition-colors cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedCategories(prev => prev.filter(c => c !== catCode));
                              }}
                            />
                          </span>
                        );
                      })
                    )}

                    <ChevronDown
                      size={16}
                      className={`absolute right-3 text-slate-500 transition-transform duration-300 ${isCatOpen ? 'rotate-180' : ''}`}
                    />
                  </div>

                  {/* Dropdown Menu */}
                  {isCatOpen && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setIsCatOpen(false)} />

                      <div
                        className="absolute z-20 mt-2 w-full max-h-60 overflow-y-auto bg-[#0F172A] border border-slate-800 rounded-2xl shadow-2xl p-2 no-scrollbar animate-in fade-in zoom-in-95 duration-200"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {PART_CATEGORIES.map((cat) => {
                          const isSelected = selectedCategories.includes(cat.code);
                          const count = aircraftParts.filter(p => p.category === cat.code).length;

                          return (
                            <div
                              key={cat.code}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedCategories(prev =>
                                  isSelected
                                    ? prev.filter(c => c !== cat.code)
                                    : [...prev, cat.code]
                                );
                              }}
                              className={`flex items-center justify-between px-3 py-2.5 rounded-lg cursor-pointer transition-colors mb-1 last:mb-0 ${
                                isSelected ? 'bg-emerald-500/10 text-emerald-400' : 'hover:bg-slate-800/50 text-slate-400'
                              }`}
                            >
                              <div className="flex items-center gap-3 min-w-0">
                                <div className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center transition-all ${
                                  isSelected ? 'bg-emerald-500 border-emerald-500' : 'border-slate-600 bg-transparent'
                                }`}>
                                  {isSelected && <Check size={12} className="text-[#020617]" strokeWidth={4} />}
                                </div>
                                <span className="text-xs font-medium truncate">{cat.label}</span>
                              </div>
                              <span className="text-[10px] font-mono opacity-40 ml-2 flex-shrink-0">({count})</span>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>

                {/* Price Range */}
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2 block ml-1">
                    {getDictValue("listing.part.filter.budget.txb") || "Buget"}
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number" placeholder="Min €"
                      className="h-10 px-3 rounded-lg bg-[#1E293B] border border-slate-700 text-xs text-white outline-none focus:border-emerald-500"
                      value={priceMin} onChange={(e) => setPriceMin(e.target.value)}
                    />
                    <input
                      type="number" placeholder="Max €"
                      className="h-10 px-3 rounded-lg bg-[#1E293B] border border-slate-700 text-xs text-white outline-none focus:border-emerald-500"
                      value={priceMax} onChange={(e) => setPriceMax(e.target.value)}
                    />
                  </div>
                </div>

                {/* Condition State */}
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-3 block ml-1">
                    {getDictValue("listing.part.filter.state.txb") || "Condiție tehnică"}
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {partStatesList.map(s => {
                      const isSelected = selectedStates.includes(s.code);
                      return (
                        <button
                          key={s.code}
                          onClick={() => setSelectedStates(prev => isSelected ? prev.filter(x => x !== s.code) : [...prev, s.code])}
                          className={`py-2 rounded-lg text-[9px] font-bold uppercase border transition-all ${
                            isSelected ? "border-emerald-500 bg-emerald-500/10 text-emerald-400" : "border-slate-800 bg-slate-900/40 text-slate-600"
                          }`}
                        >
                          {s.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Sorting */}
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2 block ml-1">
                    {getDictValue("listing.part.filter.sort.txb") || "Sortare"}
                  </label>
                  <select
                    value={sort}
                    onChange={(e) => setSort(e.target.value as SortOption)}
                    className="w-full h-11 px-4 rounded-xl bg-[#1E293B] border border-slate-700 text-sm text-white focus:border-emerald-500 outline-none appearance-none cursor-pointer"
                  >
                    <option value="price-asc">
                      {getDictValue("listing.part.filter.sort.cheapest") || "Preț: Crescător"}
                    </option>
                    <option value="price-desc">
                      {getDictValue("listing.part.filter.sort.costliest") || "Preț: Descrescător"}
                    </option>
                    <option value="qty-desc">Stoc: Descrescător</option>
                    <option value="title-asc">Nume: A-Z</option>
                  </select>
                </div>

                {/* SUMAR REZULTATE */}
                <div className="pt-4 border-t border-slate-800/50">
                  <div className="bg-slate-950/50 rounded-2xl p-4 border border-slate-800 flex items-center gap-4">
                    <div className={`p-2 rounded-lg ${filteredParts.length > 0 ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
                      <Activity size={16} className={filteredParts.length > 0 ? 'text-emerald-500' : 'text-red-500'} />
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-500 font-bold uppercase">
                        {getDictValue("listing.part.filter.match.txb") || "Unități găsite"}
                      </p>
                      <p className="text-sm font-black text-white italic">
                        {filteredParts.length} {getDictValue("listing.part.filter.match.results") || "Rezultate"}
                      </p>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </aside>

          {/* MAIN GRID */}
          <main className="lg:col-span-9">
            {filteredParts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-32 bg-slate-900/10 rounded-[3rem] border border-dashed border-slate-800">
                <Package size={32} className="text-slate-700 mb-6" />
                <h4 className="text-white font-black uppercase tracking-widest mb-2">
                  {getDictValue("home.search.noResults") || "Nu s-au găsit piese"}
                </h4>
                <p className="text-slate-500 text-sm">Încearcă să resetezi filtrele de căutare.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
                {filteredParts.map(part => (
                  <div key={part.partId || part.serialNumber || part.title} className="transition-transform hover:scale-[1.02] active:scale-[0.98]">
                    <AircraftPartCard part={part} />
                  </div>
                ))}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default AircraftPartCategoryPage;